import i18n from "../i18n";
import useReaderStore from "../context/readerStore";
import { isFeatureAllowed } from "./drmPolicyService";
import {
  createUnderlineApi,
  deleteUnderlineApi,
  fetchAccessData,
  getSessionTokens,
  getValidRstToken,
} from "./igpService";
import {
  showFeatureNotAllowedNotification,
  showOperationFailedNotification,
  showSessionExpiredNotification,
} from "./notificationService";

/**
 * Underline service for managing EPUB text underlines.
 * Uses IGP API as source-of-truth.
 */

export async function refreshUnderlinesFromApi() {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    const accessData = await fetchAccessData(grantId, rstToken, ["underline"]);
    const underlines = accessData?.underline?.data || [];
    const { setAnnotationData } = useReaderStore.getState();
    setAnnotationData("underlines", underlines);
    return underlines;
  } catch (err) {
    console.error("Error refreshing underlines from API:", err);
    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    }
    return [];
  }
}

/**
 * Internal: Persist underline to API in background.
 */
async function _persistUnderlineToApi(underlineId, payload) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    await createUnderlineApi(grantId, rstToken, payload);
  } catch (err) {
    console.error("Error persisting EPUB underline:", err);
    // Rollback: Remove from store
    const state = useReaderStore.getState();
    const current = state.annotations.underlines.data || [];
    state.setAnnotationData(
      "underlines",
      current.filter((u) => u.id !== underlineId),
    );

    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.saveUnderline"));
    }
  }
}

/**
 * Creates a new underline annotation with OPTIMISTIC UPDATE.
 */
export function createUnderline(underlineData) {
  const requestId =
    underlineData.id || `ul_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Enforce DRM policy
  const drmPolicy = useReaderStore.getState().drmPolicy;
  if (!isFeatureAllowed(drmPolicy, "underline")) {
    showFeatureNotAllowedNotification("underline");
    return null;
  }

  const optimisticUnderline = {
    ...underlineData,
    id: requestId,
    timestamp: new Date().toISOString(),
    _isPending: true,
  };

  // Update store immediately
  const state = useReaderStore.getState();
  const current = state.annotations.underlines.data || [];
  state.setAnnotationData("underlines", [...current, optimisticUnderline]);

  // Background sync
  _persistUnderlineToApi(requestId, { ...underlineData, id: requestId });

  return optimisticUnderline;
}

/**
 * Deletes an underline by ID with OPTIMISTIC UPDATE.
 */
export function deleteUnderline(underlineId) {
  const state = useReaderStore.getState();
  const current = state.annotations.underlines.data || [];
  const underlineToDelete = current.find((u) => u.id === underlineId);

  if (!underlineToDelete) return null;

  // Remove from store immediately
  state.setAnnotationData(
    "underlines",
    current.filter((u) => u.id !== underlineId),
  );

  // Background sync
  _persistUnderlineDelete(underlineId, underlineToDelete);

  return underlineToDelete;
}

/**
 * Internal: Persist underline deletion to API in background.
 */
async function _persistUnderlineDelete(underlineId, underlineData) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    await deleteUnderlineApi(grantId, rstToken, underlineId);
  } catch (err) {
    console.error("Error persisting EPUB underline delete:", err);
    // Rollback: Re-add to store
    const state = useReaderStore.getState();
    const current = state.annotations.underlines.data || [];
    state.setAnnotationData("underlines", [...current, underlineData]);

    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.deleteUnderline"));
    }
  }
}


export default useReaderStore;
